import { useQueryClient } from '@tanstack/react-query';
import { useUserStore } from '@/store/userStore';
import { scheduleClassesService } from '@/services/scheduleClassesService';
import { ScheduledClass } from '@/types/types';

/**
 * Hook to manage class data fetching
 * Invalidates schedule caches (service + React Query) so any mounted view refetches.
 */
export const useClassData = (
  classId: string,
  classes: ScheduledClass[]
) => {
  const { user, gymId } = useUserStore();
  const queryClient = useQueryClient();

  const selectedClass = classes.find(c => c.id === classId);

  const refreshClassData = async () => {
    if (!user?.id || !gymId) {
      console.warn('[refreshClassData] Missing user or gymId');
      return;
    }

    console.log('[refreshClassData] Invalidating schedule caches for booking/capacity update');

    // 1. Vider le cache mémoire du service
    scheduleClassesService.invalidateCache(user.id, gymId);

    // 2. Invalider toutes les queries React Query liées au planning
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['schedule-classes', user.id, gymId] }),
      queryClient.invalidateQueries({ queryKey: ['class-availability'] }),
    ]);

    // 3. Forcer un refetch immédiat des queries actives
    await queryClient.refetchQueries({
      queryKey: ['schedule-classes', user.id, gymId],
      type: 'active',
    });
  };

  return {
    selectedClass,
    refreshClassData,
  };
};
